// Bucățile unui setup care apar și pe cardul din Формации, și în vederea mare:
// planul (intrare / stop / țintă / comision) și fluxul de pe patru burse. Într-un singur
// loc, ca cele două să nu ajungă să spună lucruri diferite despre aceeași monedă.

import { el, clear, fmtPrice, fmtVolume } from '../core/util.js';
import { PLAN_COLORS, planLegendFor } from '../chart/whales.js';
import { iconMoneda } from './coin-icon.js';
import { icon } from './icons.js';
import { baseTicker, toast } from './components.js';

/**
 * Butoanele unei monede pe carduri: numele copiază tickerul, ochiul o deschide în
 * „Список наблюдения". Înainte numele făcea a doua treabă — iar copierea, de care ai
 * nevoie ca să cauți moneda în terminal, nu exista deloc.
 */
export function coinButtons(item, onOpen, size = 16) {
  return [
    el('button', {
      class: 'idea-symbol',
      title: `Скопировать тикер ${item.symbol}`,
      onclick: (e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(item.symbol)
          .then(() => toast(`Тикер ${item.symbol} скопирован`, 'success'))
          .catch(() => toast('Браузер не дал доступа к буферу обмена', 'error'));
      },
    }, [iconMoneda(item.symbol, size), el('span', { text: baseTicker(item.symbol) }), icon('clipboard', 10)]),
    el('button', {
      class: 'coin-open',
      title: 'Открыть в «Списке наблюдения»',
      onclick: (e) => { e.stopPropagation(); onOpen?.(); },
    }, [icon('eye', 12)]),
  ];
}

export const BURSE = [
  ['binance', 'Binance'], ['bybit', 'Bybit'], ['okx', 'OKX'], ['bitget', 'Bitget'],
];

export const semn = (v, cifre = 0) => `${v >= 0 ? '+' : ''}${Number(v).toFixed(cifre)}`;

/** Intrare / stop / țintă, cu prețul fiecăruia și ce înseamnă comisionul. */
export function renderPlan(host, it) {
  clear(host);
  if (it.plans) {
    // Apropiere de nivel: două planuri, unul pentru fiecare deznodământ.
    const rez = it.level_kind === 'resistance';
    for (const [nume, p, cls] of [
      [rez ? 'Пробой вверх (LONG)' : 'Пробой вниз (SHORT)', it.plans.breakout, 'brk'],
      [rez ? 'Отскок вниз (SHORT)' : 'Отскок вверх (LONG)', it.plans.bounce, 'bnc'],
    ]) {
      host.append(el('div', { class: `fs-plan ${cls}` }, [
        el('b', { text: nume }),
        el('span', { text: `${p.type === 'stop' ? 'стоп-ордер' : 'лимит'} ${fmtPrice(p.entry)} · стоп ${fmtPrice(p.stop)}` }),
        el('span', { text: `цель 1 (½) ${fmtPrice(p.target1 ?? p.target)} · цель 2 ${fmtPrice(p.target)}` }),
        el('span', { class: 'fs-hist', text: `стоп ${p.stop_pct}% · комиссия ${Math.round((p.fee_r || 0) * 100)}% стопа` }),
      ]));
    }
    return;
  }
  const p = it.plan;
  if (!p) return;
  const lung = it.direction === 'long';
  const pct = (x) => `${semn((x - p.entry) / p.entry * 100, 2)}%`;
  const feePct = Math.round((p.fee_r || 0) * 100);
  const cell = (k, v, sub, cls = '') => el('div', { class: `form-plan-c ${cls}`, title: `${k}: ${v}${sub ? ` — ${sub}` : ''}` }, [
    el('span', { class: 'form-num-k', text: k }),
    el('span', { class: 'form-num-v', text: v }),
    sub ? el('span', { class: 'form-plan-sub', text: sub }) : null,
  ]);
  const risc = Math.abs(p.entry - p.stop);
  const inR = (x) => (risc ? `${(Math.abs(x - p.entry) / risc).toFixed(1)}R` : '');
  host.append(
    cell('Вход', fmtPrice(p.entry),
      p.type === 'stop' ? `стоп-ордер, пробой ${lung ? 'вверх' : 'вниз'}` : p.type === 'limit' ? 'лимит' : 'по рынку'),
    cell('Стоп', fmtPrice(p.stop), pct(p.stop), 'down'),
    p.target1 ? cell('Цель 1 · ½', fmtPrice(p.target1), `${pct(p.target1)} · ${inR(p.target1)} · стоп→вход`, 'up') : null,
    cell(p.target1 ? 'Цель 2' : 'Цель 2R', fmtPrice(p.target), `${pct(p.target)} · ${inR(p.target)} · остаток`, 'up'),
    cell('Комиссия', `${feePct}%`, 'от стопа, за круг',
      feePct > 45 ? 'down' : feePct > 30 ? 'warn' : ''),
  );
}

/**
 * Cine împinge, pe ce bursă. Bara arată cota fiecărei burse din volumul pe 15 minute,
 * colorată după cum a cumpărat sau a vândut agresiv — dintr-o privire se vede dacă
 * Bybit, unde tranzacționezi, e doar o fracțiune din mișcare.
 */
export function renderFlow(host, f, direction) {
  clear(host);
  if (!f) {
    host.append(el('span', { class: 'form-flow-wait', text: 'Поток 4 бирж: подключаюсь к монете…' }));
    return;
  }
  const lung = direction !== 'short';
  // Aceleași praguri ca pe server: înainte de ele delta e o cifră pe câteva tranzacții.
  const strans = f.warm >= 5 && f.vol5 >= 25000;
  const d = (v, k) => {
    if (v === null || v === undefined) return el('span', { class: 'form-flow-v mute', text: `${k} —` });
    if (!strans) return el('span', { class: 'form-flow-v mute', text: `${k} ${semn(v * 100)}%` });
    const pro = (v >= 0) === lung;
    return el('span', {
      class: `form-flow-v ${Math.abs(v) < 0.05 ? 'mute' : pro ? 'up' : 'down'}`,
      title: 'Агрессивные покупки минус продажи, доля от объёма, сумма по 4 биржам',
      text: `${k} ${semn(v * 100)}%`,
    });
  };

  const bara = el('div', { class: 'form-flow-bar' });
  for (const [ex, nume] of BURSE) {
    const r = f.exchanges?.[ex];
    if (!r || !r.share) continue;
    const cumpara = r.delta15 >= 0;
    const seg = el('div', {
      class: `form-flow-seg ${cumpara ? 'buy' : 'sell'}`,
      title: `${nume}: ${Math.round(r.share * 100)}% объёма за 15 мин, дельта ${fmtVolume(r.delta15)}`,
    }, [el('span', { text: `${nume} ${Math.round(r.share * 100)}%` })]);
    seg.style.flexGrow = String(Math.max(r.share, 0.04));
    bara.append(seg);
  }

  host.append(el('div', { class: 'form-flow-row' }, [
    el('span', { class: 'form-flow-k', text: 'Поток 4 бирж' }),
    d(f.delta1, '1м'), d(f.delta5, '5м'), d(f.delta15, '15м'),
    f.spike !== null && f.spike !== undefined
      ? el('span', {
        class: `form-flow-v ${f.spike >= 2 ? 'up' : 'mute'}`, text: `объём ×${f.spike}`,
        title: 'Средний объём за последние 5 минут к среднему за час до этого',
      })
      : el('span', {
        class: 'form-flow-v mute',
        title: 'Поток монеты слушается с момента, как её нашёл сканер или открыли этот график. '
          + 'Дельта надёжна через 5 минут, всплеск объёма — через 15.',
        text: f.warm < 5 ? `сбор потока ${f.warm}/5 мин` : `объём: разогрев ${f.warm}/15 мин`,
      }),
    f.oi15 !== null && f.oi15 !== undefined
      ? el('span', {
        class: `form-flow-v ${f.oi15 >= 0.5 ? 'up' : f.oi15 <= -0.5 ? 'down' : 'mute'}`,
        title: 'Изменение открытого интереса, сумма по 4 биржам',
        text: `OI 15м ${semn(f.oi15, 2)}%`,
      })
      : null,
  ]));
  if (bara.children.length) host.append(bara);
}

/** Liniile planului pe un grafic: nivelul formației, stopul, ținta, intrarea. */
export function drawPlanLines(chart, it, levelColor, previous = [], { width = 1 } = {}) {
  const c = chart?.candles;
  if (!c || !it) return [];
  for (const l of previous) c.removePriceLine(l);
  const linii = [];
  const linie = (price, color, title, style, w = width) => {
    if (price === null || price === undefined) return;
    linii.push(c.createPriceLine({
      price, color, lineWidth: w, lineStyle: style,
      axisLabelVisible: false,   // valorile sunt deja în rândul planului
      title,
    }));
  };
  linie(it.level, levelColor, it.kind === 'impulse' ? 'основание импульса' : 'уровень', 2, 1);
  if (it.plans) {
    linie(it.plans.breakout.entry, PLAN_COLORS.entry, 'вход пробоя', 1);
    linie(it.plans.breakout.stop, PLAN_COLORS.stop, 'стоп пробоя', 2, 1);
    linie(it.plans.bounce.entry, PLAN_COLORS.bounceEntry, 'вход отскока', 1);
    // Stopul ricoșeului are culoarea lui: cu doi stopi roșii nu știai care e al cui.
    linie(it.plans.bounce.stop, PLAN_COLORS.bounceStop, 'стоп отскока', 2, 1);
  }
  if (it.plan) {
    linie(it.plan.stop, PLAN_COLORS.stop, 'стоп', 0);
    if (it.plan.target1) linie(it.plan.target1, PLAN_COLORS.target1, 'цель 1', 2);
    linie(it.plan.target, PLAN_COLORS.target, 'цель 2', 0);
    if (it.plan.type === 'stop') linie(it.plan.entry, PLAN_COLORS.entry, 'вход', 1);
  }
  return linii;
}

/**
 * Legenda scurtă de sub un grafic mic: ce înseamnă liniile lui, într-un rând.
 * Pe graficele mici numele liniilor nu încap pe axă, deci fără ea liniile sunt mute.
 */
export function miniLegend(kind) {
  return el('div', { class: 'mini-legend' }, planLegendFor(kind).filter((l) => l.short).map((l) => {
    const sw = el('span', { class: `sv-sw sw-${l.shape}` });
    sw.style.setProperty('--c', l.color);
    return el('span', { class: 'ml-item', title: l.text }, [sw, el('span', { text: l.short })]);
  }));
}
